import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Reveal } from "@/components/Reveal";
import { useContact } from "@/components/useContent";
import { Plus } from "lucide-react";

const FAQS = (contactEmail) => [
  {
    q: "What are the different beat licensing tiers available?",
    a: "We offer tiered licensing to fit your project scope, ranging from MP3 and WAV leases to Unlimited Leases and Exclusive Rights. Each tier comes with specific streaming limits, trackout stems (for WAV/Unlimited), and legal permission for commercial release. Click \"View Licensing Agreement\" under any beat to review full terms before purchase.",
    tag: "Licensing",
  },
  {
    q: "Do I still need to give credit when using a DGE beat?",
    a: "Yes! Proper artist and producer credit is mandatory for all commercial releases. You must credit \"(Prod. by Heather Da Boa)\" or \"Produced by DGE: Cold-Blooded Records LLC™\" in all digital distribution metadata (Spotify, Apple Music, YouTube, etc.) and social media descriptions.",
    tag: "Licensing",
  },
  {
    q: "Are DGE beats registered with ASCAP and PROs?",
    a: "Absolutely. As official ASCAP® & PRO members, all our compositions are properly tracked. When releasing your finished song, ensure songwriting and publishing splits are registered correctly according to your license agreement.",
    tag: "Licensing",
  },
  {
    q: "What is the standard turnaround time for mixing and mastering?",
    a: "Our standard turnaround time is 3 to 5 business days once we receive your fully exported audio files and project notes. Rush delivery options are available upon request for an additional fee.",
    tag: "Mixing & Mastering",
  },
  {
    q: "How should I prepare and send my audio stems for mixing?",
    a: `For best results, export all audio files as dry, 24-bit WAV stems starting from the exact same point (bar 1 / 0:00). Ensure your individual track levels are peaking between -6dB and -12dB with zero clipping or master effects/limiters turned on. Email them to ${contactEmail} after booking.`,
    tag: "Mixing & Mastering",
  },
  {
    q: "How many revisions are included with a mix and master package?",
    a: "Every standard mixing and master package includes up to two (2) rounds of minor revisions to ensure the sound hits precisely how you want it. Additional revisions outside the scope of the original notes may incur a small hourly studio fee.",
    tag: "Mixing & Mastering",
  },
];

const FAQItem = ({ item, open, onToggle, index }) => (  <div
    className={`border rounded-xl transition-colors duration-300 ${open ? "border-venom/50 bg-charcoal" : "border-crystal/20 bg-charcoal/60 hover:border-crystal/45"}`}
    data-testid={`faq-item-${index}`}
  >
    <button
      onClick={onToggle}
      className="w-full flex items-center justify-between gap-4 px-5 sm:px-6 py-5 text-left"
      aria-expanded={open}
      data-testid={`faq-toggle-${index}`}
    >
      <div className="flex items-center gap-3 min-w-0">
        <span className={`font-mono text-[9px] uppercase tracking-[0.15em] rounded-full px-2.5 py-1 border shrink-0 ${open ? "border-venom/50 text-venom" : "border-crystal/40 text-crystal"}`}>
          {item.tag}
        </span>
        <span className="font-semibold text-white text-sm sm:text-base">{item.q}</span>
      </div>
      <Plus
        size={18}
        className={`shrink-0 transition-transform duration-300 ${open ? "rotate-45 text-venom" : "text-ice/50"}`}
      />
    </button>
    <AnimatePresence initial={false}>
      {open && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          className="overflow-hidden"
        >
          <p className="px-5 sm:px-6 pb-6 text-sm text-ice/70 leading-relaxed" data-testid={`faq-answer-${index}`}>
            {item.a}
          </p>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

export const FAQSection = () => {
  const [open, setOpen] = useState(0);
  const contact = useContact();
  const faqs = FAQS(contact.email);

  return (
    <section id="faq" className="relative py-20 lg:py-28" data-testid="faq-section">
      <div className="max-w-4xl mx-auto px-4 sm:px-8">
        <Reveal className="text-center">
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-venom font-medium mb-4">
            FAQ
          </p>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white leading-tight">
            Straight answers.
            <br />
            <span className="text-stroke-ice">No fine print games.</span>
          </h2>
        </Reveal>

        <div className="mt-12 space-y-3" data-testid="faq-list">
          {faqs.map((item, i) => (
            <Reveal key={item.q} delay={i * 0.05}>
              <FAQItem item={item} index={i} open={open === i} onToggle={() => setOpen(open === i ? -1 : i)} />
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};
